import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, PlayCircle, Flame, HeartPulse, Shield } from 'lucide-react';
import { MarketingImage } from '@/components/marketing/MarketingImage';
import { HeroMapMockup } from '@/components/marketing/mockups/HeroMapMockup';

const ORG_BULLETS = [
  {
    icon: Flame,
    color: 'text-accent-emergency',
    label: 'Feuerwehr',
    text: 'Brandbekämpfung, technische Rettung',
  },
  {
    icon: HeartPulse,
    color: 'text-orange-400',
    label: 'Rettungsdienst',
    text: 'Patientenversorgung mit echten BOS-Funkstati',
  },
  {
    icon: Shield,
    color: 'text-accent-dispatch',
    label: 'Polizei',
    text: 'Festnahmen, SEK-Einsätze, Geiselnahmen',
  },
];

export function Hero() {
  return (
    <section
      id="top"
      className="relative flex min-h-[90vh] items-center overflow-hidden pt-20 md:pt-24"
    >
      {/* Subtle background gradient */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-accent-emergency/5 to-transparent" />
        <div className="absolute -left-32 top-32 h-72 w-72 rounded-full bg-accent-emergency/10 blur-3xl" />
        <div className="absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-accent-dispatch/10 blur-3xl" />
      </div>

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-4 md:grid-cols-2 md:px-6">
        {/* Left: text column */}
        <motion.div
          initial={{ opacity: 0, x: -32 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-border-subtle bg-bg-overlay px-3 py-1 text-xs uppercase tracking-widest text-fg-secondary backdrop-blur-sm">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-emergency" />
            Live-Dispatch • Browser • Kostenlos
          </p>

          <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
            Deine Stadt.
            <br />
            Deine Einsätze.
            <br />
            <span className="bg-gradient-to-r from-accent-emergency via-orange-400 to-accent-dispatch bg-clip-text text-transparent">
              Echtzeit.
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-lg text-fg-secondary md:text-xl">
            Die moderne Leitstellen-Simulation für Feuerwehr, Rettungsdienst und Polizei.
            Disponiere echte Einsätze auf echten Karten — als einer von Tausenden in deiner
            Region.
          </p>

          <ul className="mt-8 space-y-3">
            {ORG_BULLETS.map((b) => {
              const Icon = b.icon;
              return (
                <li key={b.label} className="flex items-start gap-3">
                  <span
                    className={[
                      'mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-bg-elevated/60',
                      b.color,
                    ].join(' ')}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <p className="text-base text-fg-secondary">
                    <span className="font-semibold text-fg-primary">{b.label}:</span>{' '}
                    {b.text}
                  </p>
                </li>
              );
            })}
          </ul>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/play"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-accent-emergency px-6 py-3 text-base font-semibold text-white shadow-md transition hover:bg-accent-emergency-hover hover:shadow-[0_0_32px_rgba(220,38,38,0.45)]"
            >
              Jetzt kostenlos spielen
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a
              href="#organizations"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border-strong bg-bg-elevated/60 px-6 py-3 text-base font-semibold text-fg-primary backdrop-blur-sm transition hover:border-border-strong hover:bg-bg-elevated"
            >
              <PlayCircle className="h-4 w-4" />
              Mehr erfahren
            </a>
          </div>
        </motion.div>

        {/* Right: hero image / mockup */}
        <motion.div
          initial={{ opacity: 0, x: 32 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative rounded-xl border border-border-subtle bg-bg-secondary/40 p-2 shadow-2xl backdrop-blur-md">
            <MarketingImage
              id="heroMap"
              mockupComponent={HeroMapMockup}
              alt="Live-Karte mit drei aktiven Einsätzen in Deutschland"
              eager
              width={800}
              height={500}
            />
          </div>
          <div className="pointer-events-none absolute -inset-4 -z-10 rounded-2xl bg-gradient-to-tr from-accent-emergency/10 via-transparent to-accent-dispatch/10 blur-2xl" />
        </motion.div>
      </div>
    </section>
  );
}
